/**
 * Edit Plan Modal
 * Form to edit an existing subscription plan
 */

import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import planService, { Plan, UpdatePlanRequest } from '../../services/api/planService';

interface EditPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: Plan;
}

export const EditPlanModal = ({ isOpen, onClose, plan }: EditPlanModalProps) => {
  const queryClient = useQueryClient();

  const [form, setForm] = useState<UpdatePlanRequest>({
    name: plan.name,
    description: plan.description ?? '',
    isActive: plan.isActive,
  });

  useEffect(() => {
    if (plan) {
      setForm({
        name: plan.name,
        description: plan.description ?? '',
        isActive: plan.isActive,
      });
    }
  }, [plan]);

  const updateMutation = useMutation({
    mutationFn: () => planService.updatePlan(plan.id, form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
      handleClose();
    },
  });

  const handleClose = () => {
    updateMutation.reset();
    onClose();
  };

  const handleChange = (field: keyof UpdatePlanRequest, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const isSubmitDisabled = !form.name?.trim() || updateMutation.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Plan"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary-blue"
            onClick={() => updateMutation.mutate()}
            disabled={isSubmitDisabled}
            isLoading={updateMutation.isPending}
          >
            Update Plan
          </Button>
        </>
      }
    >
      <FormContainer>
        {updateMutation.isError && (
          <ErrorMessage>
            {(updateMutation.error as any)?.response?.data?.message ||
              'Failed to update plan. Please try again.'}
          </ErrorMessage>
        )}

        <Field>
          <Label htmlFor="edit-plan-name">
            Name <Required>*</Required>
          </Label>
          <Input
            id="edit-plan-name"
            value={form.name ?? ''}
            onChange={(e) => handleChange('name', e.target.value)}
            placeholder="e.g. Starter, Professional, Enterprise"
            fullWidth
          />
        </Field>

        <Field>
          <Label htmlFor="edit-plan-description">Description</Label>
          <TextArea
            id="edit-plan-description"
            value={form.description ?? ''}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Brief description of this plan..."
            rows={3}
          />
        </Field>

        <Field>
          <StatusToggleRow>
            <StatusToggleLabel htmlFor="edit-plan-active">Active</StatusToggleLabel>
            <Toggle
              id="edit-plan-active"
              role="switch"
              aria-checked={form.isActive}
              $active={form.isActive ?? true}
              onClick={() => handleChange('isActive', !form.isActive)}
              type="button"
            >
              <ToggleThumb $active={form.isActive ?? true} />
            </Toggle>
            <StatusHint>
              {form.isActive ? 'Plan is visible and can be assigned to organizations' : 'Plan is hidden and cannot be newly assigned'}
            </StatusHint>
          </StatusToggleRow>
        </Field>
      </FormContainer>
    </Modal>
  );
};

export default EditPlanModal;

// ==================== Styled Components ====================

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Required = styled.span`
  color: ${({ theme }) => theme.colors.status.error.text};
  margin-left: 2px;
`;

const TextArea = styled.textarea`
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.borders.default};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: inherit;
  resize: vertical;
  transition: ${({ theme }) => theme.transitions.fast};
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.light};
  }
`;

const StatusToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const StatusToggleLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  white-space: nowrap;
`;

const Toggle = styled.button<{ $active: boolean }>`
  position: relative;
  width: 44px;
  height: 24px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  transition: background ${({ theme }) => theme.transitions.fast};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.primary.base : theme.colors.borders.medium};
  padding: 0;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
  }
`;

const ToggleThumb = styled.span<{ $active: boolean }>`
  position: absolute;
  top: 3px;
  left: ${({ $active }) => ($active ? '23px' : '3px')};
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffffff;
  transition: left 150ms ease-in-out;
  display: block;
`;

const StatusHint = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.4;
`;

const ErrorMessage = styled.div`
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.status.error.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.error.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.status.error.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;
